import Vue from 'vue'
import VueRouter from 'vue-router'
import { getToken } from '@/utils/auth'
// 导入AboutUs组件
import AboutUs from '@/views/about/AboutUs.vue'
import UserGuide from '@/views/user/UserGuide.vue'
import AccountManagement from '@/views/user/AccountManagement.vue'

// 修复VueRouter push重复跳转报错问题
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch((err) => {
    // 可选：记录非重复跳转的错误（如路径不存在）
    if (err.name !== 'NavigationDuplicated') {
      console.error('路由跳转错误:', err)
    }
    return err
  })
}

Vue.use(VueRouter)

// 通用参数校验函数（可扩展其他校验规则）
const validateParam = (paramName) => (to, from, next) => {
  const paramValue = to.params[paramName]
  if (typeof paramValue === 'string' && paramValue.trim() !== '') {
    next()
  } else {
    next('/chatSearch') // 可根据业务调整重定向路径
  }
}

// 路由元信息类型声明（注释说明，非必须）
/**
 * @typedef {Object} RouteMeta
 * @property {boolean} [requireAuth] - 是否需要登录验证
 * @property {string} [title] - 页面标题
 * @property {boolean} [keepAlive] - 是否需要缓存
 */

const routes = [
  {
    path: '/account-management',
    name: 'AccountManagement',
    component: AccountManagement
  },
  {
    path: '/',
    name: 'Root', // 建议命名
    redirect: '/login',
    meta: { title: '首页' } // 可选默认标题
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "login" */ '../views/login/index.vue'),
    meta: { title: '探数蚁｜AI智学助手 - 探蚁官网' }
  },
  {
    path: '/serviceItems',
    name: 'ServiceItems',
    component: () => import(/* webpackChunkName: "login" */ '../views/login/ServiceItem.vue'),
    meta: { title: '服务条款' }
  },
  {
    path: '/downloadDesk',
    name: 'DownloadDesk',
    component: () => import('../components/DownloadDesk.vue')
  },
  {
    path: '/collect',
    name: 'Collect',
    component: () => import('../components/mobile/collect.vue')
  },
  {
    path: '/level',
    name: 'Level',
    component: () => import('../components/mobile/level.vue')
  },
  {
    path: '/privatePolicy',
    name: 'PrivatePolicy',
    component: () => import(/* webpackChunkName: "login" */ '../views/login/PrivatePolicy.vue'),
    meta: { title: '隐私政策' }
  },
  {
    path: '/pay/vip-subscription',
    name: 'VipSubscription',
    meta: { title: '会员支付' },
    component: () => import('../views/pay/VipSubscription.vue')
  },
  {
    path: '/pay/orders',
    name: 'orders',
    meta: { title: '会员订单' },
    component: () => import('../views/pay/orders.vue')
  },

  {
    path: '/about-us',
    name: 'AboutUs',
    component: AboutUs,
    meta: {
        title: '关于我们', // 浏览器标签标题
        requireAuth: false // 根据需求配置是否需要登录
    }
  },

  {
    path: '/user-guide',
    name: 'UserGuide',
    component: UserGuide,
    meta: {
      requireAuth: true // 如果需要登录后才能访问，添加该配置
    }
  },

  {
    path: '/chatSearch',
    component: () => import(/* webpackChunkName: "chat-search" */ '../views/chatSearch/index.vue'),
    meta: {
      requireAuth: true,
      title: '聊天搜索'
    },
    children: [
      {
        path: '',
        name: 'ChatSearchRedirect',
        redirect: '/chatSearch/exercises', // 注意：需确保/exercises路由存在
        meta: { requireAuth: true }
      },
      {
        path: ':robotId',
        component: () => import(/* webpackChunkName: "chat-detail" */ '../views/chatSearch/sub/chatList.vue'),
        beforeEnter: validateParam('robotId'), // 使用通用校验函数
        meta: {
          requireAuth: true,
          // keepAlive: true,
          title: '聊天详情'
        },
        children: [
          {
            path: '',
            name: 'ChatMainRedirect',
            redirect: (to) => `/chatSearch/${to.params.robotId}/${+new Date()}`, // 动态重定向
            meta: { keepAlive: true }
          },
          {
            path: ':sessionId',
            name: 'ChatSessionDetail',
            component: () => import(/* webpackChunkName: "chat-session" */ '../components/chat/chatMain.vue'),
            beforeEnter: validateParam('sessionId'), // 使用通用校验函数
            meta: {
              requireAuth: true,
              keepAlive: true,
              title: '探数蚁｜AI智学助手 - 探蚁官网'
            }
          }
        ]
      }
    ]
  },

  {
    path: '/drag',
    name: 'Drag',
    component: () => import(/* webpackChunkName: "recorder" */ '../components/drag.vue'),
    meta: { title: '题目' }
  },

  // {
  //   path: '/recorder',
  //   name: 'Recorder',
  //   component: () => import(/* webpackChunkName: "recorder" */ '../components/record/recorder.vue'),
  //   meta: { title: '录音工具' }
  // },
  {
    path: '/resolveMD',
    name: 'ResolveMD',
    component: () => import(/* webpackChunkName: "resolve-md" */ '../views/test/resolveMD.vue'),
    meta: { title: 'Markdown解析' }
  },
  // // 测试路由组（建议添加分组注释）
  // {
  //   path: '/tree1',
  //   name: 'Tree1',
  //   component: () => import(/* webpackChunkName: "tree-test" */ '../views/test/tree1.vue'),
  //   meta: { title: '树结构测试1' }
  // },
  // {
  //   path: '/tree2',
  //   name: 'Tree2',
  //   component: () => import(/* webpackChunkName: "tree-test" */ '../views/test/tree2.vue'),
  //   meta: { title: '树结构测试2' }
  // },
  // {
  //   path: '/tree3',
  //   name: 'Tree3',
  //   component: () => import(/* webpackChunkName: "tree-test" */ '../views/test/tree3.vue'),
  //   meta: { title: '树结构测试3' }
  // },
  // {
  //   path: '/draw',
  //   name: 'Draw',
  //   component: () => import(/* webpackChunkName: "draw-tool" */ '../components/draw/index.vue'),
  //   meta: { title: '绘图工具' }
  // }
  // {
  //   path: '/miniMind',
  //   name: 'MiniMind',
  //   component: () => import(/* webpackChunkName: "recorder" */ '../views/test/miniMind.vue'),
  //   meta: { title: '录音工具' }
  // },
  {
    path: '/gs',
    name: 'GS',
    component: () => import('../views/test/gs.vue')
  },
  {
      path: '/testDB',
      name: 'testDB',
      component: () => import(/* webpackChunkName: "recorder" */ '../views/test/testDB.vue'),
      meta: { title: '测试数据库' }
    },
]

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes,
  // 可选：添加滚动行为配置（根据业务需求）
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

// 全局前置守卫（抽离为独立函数提高可读性）
function handleBeforeEach(to, from, next) {
  // 设置页面标题（优先使用路由meta.title）
  document.title = to.meta.title || '探数蚁｜AI智学助手 - 探蚁官网'

  // 权限校验
  if (to.meta.requireAuth) {
    const token = getToken()
    // 增强校验：检查token是否为非空字符串（防御性编程）
    if (typeof token === 'string' && token.trim() !== '') {
      next()
    } else {
      next({
        path: '/login',
        query: { redirect: to.fullPath }, // 保存跳转前路径用于登录后回跳
        replace: true // 避免回退到登录前页面
      })
    }
  } else {
    next()
  }
}

router.beforeEach(handleBeforeEach)

// 全局后置钩子（添加简单埋点示例）
router.afterEach((to) => {
  // 可选：页面访问统计（需根据项目埋点系统调整）
  if (window.ga) {
    window.ga('set', 'page', to.fullPath)
    window.ga('send', 'pageview')
  }
})

export default router